import { FSRSParameters } from 'ts-fsrs'
import type {
  Collection,
  CollectionWithCount,
  CollectionDisplay,
  CollectionId,
  WordStatus,
  CardInfo,
  BookmarkInfo,
  UserCollectionSettings,
  VirtualListType,
  CuratedCollection
} from '@/types/collections'
import type { SearchResult } from '@/types/dictionary'
import {
  getCollectionType,
  getCuratedSlug,
  getVirtualType
} from '@/types/collections'
import {
  createStudyDeck,
  deleteStudyDeck,
  ensureDefaultStudyDeck,
  getAllStudyDecks,
  getStudyDeckById,
  getStudyDeckItems,
  getUserStudyDecks,
  getVirtualDeckDescription,
  getVirtualDeckName,
  removeWordFromDeck,
  removeWordFromDefaultDeck,
  saveWordToDeck,
  saveWordToDefaultDeck,
  updateStudyDeck,
  areWordsInAnyDeck,
  areWordsSaved,
  getWordDeckMembership,
  isWordInDeck,
  isWordSaved,
  cardInfoFromDeckItem
} from './study'
import {
  getCuratedCollection as loadCuratedCollection,
  getAllCuratedCollections as loadAllCuratedCollections
} from '@/content/curated-collections'
import { supabase } from './supabase'

export { getCollectionType, getCuratedSlug, getVirtualType }

type StudyNoteCardIdRow = {
  card?: { id: string } | { id: string }[] | null
}

type StudyNoteStatusRow = {
  dict_entry_id: number | null
  card?: { state: number | null } | { state: number | null }[] | null
}

type StudyNoteCardInfoRow = {
  dict_entry_id: number | null
  card?:
    | {
        due: string
        last_review: string | null
        reps: number
        lapses: number
        state: number
        front: string
        back: string | null
      }
    | {
        due: string
        last_review: string | null
        reps: number
        lapses: number
        state: number
        front: string
        back: string | null
      }[]
    | null
}

function firstJoin<T>(value: T | T[] | null | undefined): T | null {
  return Array.isArray(value) ? (value[0] ?? null) : (value ?? null)
}

export function isUserCollection(id: CollectionId): boolean {
  return getCollectionType(id) === 'user'
}

export function isCuratedCollection(id: CollectionId): boolean {
  return getCollectionType(id) === 'curated'
}

export function isVirtualCollection(id: CollectionId): boolean {
  return getCollectionType(id) === 'virtual'
}

function deckToCollection(
  deck: Awaited<ReturnType<typeof ensureDefaultStudyDeck>>
): Collection {
  return {
    id: deck.id,
    user_id: deck.user_id,
    name: deck.name,
    description: deck.description,
    is_bookmark: deck.default_kind === 'saved_words',
    fsrs_params: deck.fsrs_params,
    new_cards_limit: deck.new_cards_limit,
    review_limit: deck.review_limit,
    rollover_hour: deck.rollover_hour,
    created_at: deck.created_at,
    updated_at: deck.updated_at
  }
}

export async function getUserCollections(
  _userId?: string
): Promise<CollectionWithCount[]> {
  void _userId
  const decks = await getUserStudyDecks()
  return decks.map((deck) => ({
    ...deckToCollection(deck),
    is_bookmark: deck.default_kind === 'saved_words',
    item_count: deck.item_count
  }))
}

export async function createCollection(
  userId: string,
  name: string,
  description?: string
): Promise<Collection> {
  return deckToCollection(await createStudyDeck(userId, name, description))
}

export async function updateCollection(
  userId: string,
  collectionId: string,
  updates: { name?: string; description?: string }
): Promise<Collection> {
  return deckToCollection(await updateStudyDeck(userId, collectionId, updates))
}

export async function deleteCollection(
  userId: string,
  collectionId: string
): Promise<void> {
  await deleteStudyDeck(userId, collectionId)
}

export async function getCollectionById(
  id: CollectionId,
  userId: string | null
): Promise<CollectionDisplay | null> {
  const deck = await getStudyDeckById(id, userId)
  if (!deck) return null
  return {
    ...deck,
    is_bookmark: deck.default_kind === 'saved_words'
  }
}

export async function getOrCreateBookmarkCollection(
  _userId?: string
): Promise<Collection> {
  void _userId
  return deckToCollection(await ensureDefaultStudyDeck('saved_words'))
}

export async function isEntryBookmarked(
  _userId: string,
  entryId: number
): Promise<boolean> {
  void _userId
  return isWordSaved(entryId)
}

export async function addBookmark(
  _userId: string,
  entryId: number
): Promise<void> {
  void _userId
  await saveWordToDefaultDeck(entryId)
}

export async function removeBookmark(
  _userId: string,
  entryId: number
): Promise<void> {
  void _userId
  await removeWordFromDefaultDeck(entryId)
}

export async function addEntryToCollection(
  _userId: string,
  collectionId: string,
  entryId: number
): Promise<void> {
  void _userId
  await saveWordToDeck(collectionId, entryId)
}

export async function removeEntryFromCollection(
  _userId: string,
  collectionId: string,
  entryId: number
): Promise<void> {
  void _userId
  await removeWordFromDeck(collectionId, entryId)
}

export async function resetEntryProgress(
  _userId: string,
  entryId: number
): Promise<void> {
  void _userId
  const { data, error } = await supabase
    .from('study_notes')
    .select('card:study_cards(id)')
    .eq('note_type', 'word')
    .eq('dict_entry_id', entryId)
    .maybeSingle()

  if (error) throw error
  const card = firstJoin((data as StudyNoteCardIdRow | null)?.card)
  if (!card?.id) return

  const { error: resetError } = await supabase.rpc(
    'reset_study_card_progress',
    {
      _card_id: card.id
    }
  )
  if (resetError) throw resetError
}

export async function isEntryInCollection(
  _userId: string,
  collectionId: string,
  entryId: number
): Promise<boolean> {
  void _userId
  return isWordInDeck(collectionId, entryId)
}

export async function areEntriesBookmarked(
  _userId: string,
  entryIds: number[]
): Promise<Map<number, boolean>> {
  void _userId
  return areWordsSaved(entryIds)
}

export async function areEntriesInAnyCollection(
  _userId: string,
  entryIds: number[]
): Promise<Map<number, boolean>> {
  void _userId
  return areWordsInAnyDeck(entryIds)
}

export async function getEntryCollectionMembership(
  _userId: string,
  entryId: number,
  collectionIds: string[]
): Promise<Map<string, boolean>> {
  void _userId
  return getWordDeckMembership(entryId, collectionIds)
}

export function getCuratedCollections(): CuratedCollection[] {
  return loadAllCuratedCollections()
}

export function curatedCollectionId(slug: string): CollectionId {
  return slug
}

export async function getVirtualCollectionEntryIds(
  type: VirtualListType | 'all',
  _userId?: string
): Promise<number[]> {
  void _userId
  const result = await getStudyDeckItems(type, '', 0, 10000)
  return result
    .map((item) => item.dict_entry_id)
    .filter((id): id is number => typeof id === 'number')
}

export function getVirtualCollectionName(
  type: VirtualListType | 'all'
): string {
  return getVirtualDeckName(type)
}

export function getVirtualCollectionDescription(
  type: VirtualListType | 'all'
): string {
  return getVirtualDeckDescription(type)
}

export function virtualCollectionId(
  type: VirtualListType | 'all'
): CollectionId {
  return type
}

export async function getVirtualCollections(
  userId: string
): Promise<CollectionDisplay[]> {
  void userId
  const decks = await getAllStudyDecks(userId)
  return decks.filter((deck) => deck.type === 'virtual') as CollectionDisplay[]
}

export async function getAllCollections(
  userId: string | null
): Promise<CollectionDisplay[]> {
  const decks = await getAllStudyDecks(userId)
  return decks.map((deck) => ({
    ...deck,
    is_bookmark: deck.default_kind === 'saved_words'
  }))
}

function itemToSearchResult(
  item: Awaited<ReturnType<typeof getStudyDeckItems>>[number]
): SearchResult {
  const fields = item.fields || {}
  const definitions = Array.isArray(fields.definitions)
    ? (fields.definitions as string[])
    : []
  const simplified = String(fields.simplified || item.front || '')
  const traditional = String(fields.traditional || simplified)
  return {
    id: item.dict_entry_id || 0,
    simplified,
    traditional,
    pinyin: String(fields.pinyin || ''),
    zhuyin: typeof fields.zhuyin === 'string' ? fields.zhuyin : '',
    preview: definitions[0] || item.back || '',
    json_data: {},
    score: 0,
    definitions,
    examples: Array.isArray(fields.examples) ? fields.examples : []
  }
}

export async function getCollectionEntries(
  collectionId: CollectionId,
  userId: string | null,
  page = 0,
  limit = 50,
  _isOffline = false
): Promise<SearchResult[]> {
  void _isOffline
  if (!userId && getCollectionType(collectionId) !== 'curated') return []
  const items = await getStudyDeckItems(collectionId, userId || '', page, limit)
  return items
    .filter((item) => item.note_type === 'word' && item.dict_entry_id)
    .map(itemToSearchResult)
}

export async function getCollectionEntriesWithCards(
  collectionId: CollectionId,
  userId: string,
  page = 0,
  limit = 50,
  _listType: 'learning' | 'review' | 'all' = 'all'
): Promise<{
  entries: SearchResult[]
  cardInfoMap: Map<number, CardInfo>
}> {
  void _listType
  const items = await getStudyDeckItems(collectionId, userId, page, limit)
  const entries = items
    .filter((item) => item.note_type === 'word' && item.dict_entry_id)
    .map(itemToSearchResult)
  const cardInfoMap = new Map<number, CardInfo>()
  items.forEach((item) => {
    if (item.note_type === 'word' && item.dict_entry_id) {
      const info = cardInfoFromDeckItem(item)
      cardInfoMap.set(item.dict_entry_id, {
        dict_entry_id: item.dict_entry_id,
        due: info.due,
        last_review: info.last_review,
        reps: info.reps,
        lapses: info.lapses,
        state: info.state,
        front: info.front,
        back: info.back
      })
    }
  })
  return { entries, cardInfoMap }
}

export async function getCollectionBookmarkInfo(
  collectionId: CollectionId,
  _userId: string | null,
  entryIds: number[]
): Promise<Map<number, BookmarkInfo>> {
  void _userId
  const membership = await Promise.all(
    entryIds.map(async (entryId) => ({
      entryId,
      inDeck:
        getCollectionType(collectionId) === 'user'
          ? await isWordInDeck(collectionId, entryId)
          : false
    }))
  )
  const map = new Map<number, BookmarkInfo>()
  membership.forEach(({ entryId, inDeck }) => {
    if (inDeck) {
      map.set(entryId, {
        dict_entry_id: entryId,
        bookmarked_at: new Date().toISOString()
      })
    }
  })
  return map
}

export async function getWordStatusBulk(
  _userId: string,
  entryIds: number[]
): Promise<Map<number, WordStatus>> {
  void _userId
  if (!entryIds.length) return new Map()
  const { data, error } = await supabase
    .from('study_notes')
    .select('dict_entry_id, card:study_cards(state)')
    .eq('note_type', 'word')
    .in('dict_entry_id', entryIds)

  if (error) throw error
  const map = new Map<number, WordStatus>()
  ;((data || []) as StudyNoteStatusRow[]).forEach((row) => {
    const card = firstJoin(row.card)
    const state = card?.state
    if (!row.dict_entry_id) return
    map.set(
      row.dict_entry_id,
      state === 2 ? 'review' : state === 1 || state === 3 ? 'learning' : 'new'
    )
  })
  entryIds.forEach((id) => {
    if (!map.has(id)) map.set(id, 'new')
  })
  return map
}

export async function getGlobalCardInfoForEntries(
  _userId: string,
  entryIds: number[],
  _listType: 'learning' | 'review' | 'all' = 'all'
): Promise<Map<number, CardInfo>> {
  void _userId
  void _listType
  if (!entryIds.length) return new Map()
  const { data, error } = await supabase
    .from('study_notes')
    .select(
      'dict_entry_id, card:study_cards(id, due, last_review, reps, lapses, state, front, back)'
    )
    .eq('note_type', 'word')
    .in('dict_entry_id', entryIds)

  if (error) throw error
  const map = new Map<number, CardInfo>()
  ;((data || []) as StudyNoteCardInfoRow[]).forEach((row) => {
    const card = firstJoin(row.card)
    if (!card || !row.dict_entry_id) return
    map.set(row.dict_entry_id, {
      dict_entry_id: row.dict_entry_id,
      due: card.due,
      last_review: card.last_review,
      reps: card.reps,
      lapses: card.lapses,
      state: card.state,
      front: card.front,
      back: card.back
    })
  })
  return map
}

export async function getUserCollectionSettings(
  userId: string,
  curatedSlug: string
): Promise<UserCollectionSettings | null> {
  const { data, error } = await supabase
    .from('user_collection_settings')
    .select('*')
    .eq('user_id', userId)
    .eq('curated_slug', curatedSlug)
    .maybeSingle()

  if (error) throw error
  return data as UserCollectionSettings | null
}

export async function upsertUserCollectionSettings(
  userId: string,
  curatedSlug: string,
  settings: Partial<{
    fsrs_params: Record<string, unknown> | null
    new_cards_limit: number | null
    review_limit: number | null
    rollover_hour: number | null
  }>
): Promise<UserCollectionSettings> {
  const { data, error } = await supabase
    .from('user_collection_settings')
    .upsert(
      {
        user_id: userId,
        curated_slug: curatedSlug,
        ...settings,
        updated_at: new Date().toISOString()
      },
      { onConflict: 'user_id,curated_slug' }
    )
    .select()
    .single()

  if (error) throw error
  return data as UserCollectionSettings
}

export function resolveCollectionSettings(
  userSettings: UserCollectionSettings | null,
  defaults: {
    new_cards_limit?: number
    review_limit?: number
    rollover_hour?: number
    fsrs_params?: FSRSParameters | null
  } = {}
): {
  new_cards_limit: number
  review_limit: number
  rollover_hour: number
  fsrs_params: FSRSParameters | null
} {
  return {
    new_cards_limit:
      userSettings?.new_cards_limit ?? defaults.new_cards_limit ?? 20,
    review_limit: userSettings?.review_limit ?? defaults.review_limit ?? 200,
    rollover_hour: userSettings?.rollover_hour ?? defaults.rollover_hour ?? 4,
    fsrs_params: userSettings?.fsrs_params ?? defaults.fsrs_params ?? null
  }
}

export type CollectionProgress = {
  total: number
  learned: number
  new_remaining: number
  learn_count: number
  due_count: number
  new_limit: number
  review_limit: number
}

export async function getAllCuratedCollectionsProgress(
  _userId?: string
): Promise<Map<string, CollectionProgress>> {
  void _userId
  return new Map()
}

export async function getUserCollectionsProgress(
  _userId?: string,
  _timezone = 'UTC',
  _rolloverHour = 4
): Promise<Map<string, CollectionProgress>> {
  void _userId
  void _timezone
  void _rolloverHour
  const decks = await getUserStudyDecks()
  return new Map(
    decks.map((deck) => [
      deck.id,
      {
        total: deck.total_cards,
        learned: deck.total_cards - deck.new_cards,
        new_remaining: deck.new_cards,
        learn_count: 0,
        due_count: deck.due_cards,
        new_limit: deck.new_cards_limit,
        review_limit: deck.review_limit
      }
    ])
  )
}

export function getCuratedCollection(slug: string) {
  return loadCuratedCollection(slug)
}
